// ============================================================
// LEGACY TAB ADAPTER - a two-way bridge between the roster and the
// `Tab { id, label, target, narrativeLog, sourceCharacter }` shape the
// components still speak.
//
// This exists so the state layer can move to the roster WITHOUT touching
// a single component. Every one of the ~28 `updateTab` call sites keeps
// working: reads go out through toLegacyTab, and writes come back
// through applyLegacyTab, which splits the returned Target into the
// state half and the overrides half using the same primitives the
// session migration uses.
//
// It is deliberately temporary. Phase C replaces the call sites with
// updateCombatantState / updateCombatantSheet directly and this module
// shrinks to just combatantFromLegacyTab (still needed by migration and
// by legacy session import).
// ============================================================
import { effectiveSheet } from './stancedSheet';
import type { Character } from '../character/types';
import type { AtkProfiles, Slot, Tab } from '../../types';
import type { Combatant } from './types';
import { characterFromStatblock, combatantFromCharacter, initialWeaponAmmo } from './factories';
import { normalizeCharacter } from '../character/normalize';
import { mergeEditedOverrides, sheetFromTarget, splitTarget, stateFromTarget } from './targetSplit';
import { bumpRev } from './rev';
import { cachedDefenderSheet, projectDefender, projectLoadout } from './projection';
import { applyLoadoutSlots } from './loadoutAdapter';
import { blankProfile } from '../factories';

/** Combatant → the legacy Tab shape components read. */
export function toLegacyTab(c: Combatant): Tab {
  return {
    id: c.id,
    label: c.name,
    target: projectDefender(c),
    narrativeLog: c.narrativeLog,
    // Statblocks must present null so plain-target UI modes still trigger.
    sourceCharacter: c.kind === 'character' ? c.sheet : null,
  };
}

/**
 * A legacy Tab written back onto its Combatant.
 *
 * The Target is split back into its two halves rather than stored: what
 * combat owns lands in `state`, and anything that disagrees with the
 * resolved sheet lands in `overrides` - for the keys the write changed.
 * So a wound tick updates state, a stat edit updates overrides, and
 * neither has to be told apart at the call site.
 *
 * `weaponAmmo` is carried over explicitly - a Target has no ammo, so a
 * naive round-trip would wipe it.
 */
export function applyLegacyTab(c: Combatant, tab: Tab): Combatant {
  // This is how combat actually mutates state, so it is where the write
  // clock advances. stateFromTarget builds a FRESH state (rev 0), which
  // would otherwise reset the clock on every edit; bumpRev restores it and
  // steps it forward only when the content genuinely changed.
  const state = bumpRev(c.state, stateFromTarget(tab.target, c.state.weaponAmmo));
  // Judged against what the writer was HANDED, so a key it left alone
  // keeps its status - see mergeEditedOverrides for what that prevents.
  const overrides = mergeEditedOverrides(
    c.overrides ?? {},
    sheetFromTarget(projectDefender(c)),
    sheetFromTarget(tab.target),
    // The sheet the projection was MADE from: with a stance up that is the effective one, or the
    // stance's own deltas would be read as edits and pinned.
    cachedDefenderSheet(effectiveSheet(c)),
  );
  return {
    ...c,
    name: tab.label ?? c.name,
    narrativeLog: tab.narrativeLog ?? c.narrativeLog,
    state,
    overrides,
    updatedAt: Date.now(),
  };
}

/**
 * A legacy Tab that has no Combatant yet - a new tab, an imported
 * session, or a row being migrated.
 *
 * A bound `sourceCharacter` becomes a character-kind combatant resolving
 * from its own talents and traits; anything else gets a synthesised
 * statblock shell. Either way the Target's disagreements with that sheet
 * are preserved as overrides, so combat numbers survive exactly.
 */
export function combatantFromLegacyTab(tab: Tab, order = 0): Combatant {
  const bound: Character | null = tab.sourceCharacter ? normalizeCharacter(tab.sourceCharacter) : null;
  const sheet = bound ?? characterFromStatblock(tab.target);
  const { overrides, state } = splitTarget(tab.target, sheet, bound ? initialWeaponAmmo(bound) : {});

  const combatant = combatantFromCharacter(sheet, {
    id: tab.id,
    name: tab.label || tab.target.name,
    kind: bound ? 'character' : 'statblock',
    overrides,
    state,
    order,
  });
  combatant.narrativeLog = tab.narrativeLog ?? [];
  return combatant;
}

/** Roster → the legacy tabs array. */
export function toLegacyTabs(roster: Combatant[]): Tab[] {
  return roster.map(toLegacyTab);
}

/**
 * Reconcile a whole legacy tabs array back onto a roster - the write
 * path for setTabs-style callers (tab create/close/reorder, undo
 * restore, Firestore reconciliation, session import).
 *
 * Order follows the incoming array, so drag-reorder works for free.
 */
export function applyLegacyTabs(roster: Combatant[], tabs: Tab[]): Combatant[] {
  const byId = new Map(roster.map((c) => [c.id, c]));
  return tabs.map((tab, order) => {
    const existing = byId.get(tab.id);
    const next = existing ? applyLegacyTab(existing, tab) : combatantFromLegacyTab(tab, order);
    return next.order === order ? next : { ...next, order };
  });
}

// ────────────────────────────────────────────────────────────
// Attacker side: roster ↔ the legacy AtkProfiles shape
// ────────────────────────────────────────────────────────────

/** Shown in the attacker dropdown when nobody is being acted as. */
export const NO_ACTOR_PROFILE = 'Default';

/**
 * Display names for the attacker dropdown, disambiguated.
 *
 * AtkProfiles.list is keyed by NAME, but two combatants may legitimately
 * share one ("Ork Boy" ×3). Suffixing keeps them distinct so selecting a
 * profile still identifies exactly one combatant. Phase C's real picker
 * is keyed by id and this disappears.
 */
function profileNames(roster: Combatant[]): Map<string, string> {
  const used = new Map<string, number>();
  const byId = new Map<string, string>();
  for (const c of roster) {
    const base = c.name || 'Unnamed';
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    byId.set(c.id, seen === 0 ? base : `${base} (${seen + 1})`);
  }
  return byId;
}

/**
 * Roster → AtkProfiles.
 *
 * The attacker "profile list" IS the roster now: one entry per
 * combatant, because under the unified model you can act as anyone. A
 * synthetic Default entry covers "not acting as anyone yet", which is
 * the state a fresh session starts in.
 */
export function toLegacyAtkProfiles(
  roster: Combatant[],
  actorId: string | null,
  scratchSlots: Slot[] = blankProfile(),
): AtkProfiles {
  const names = profileNames(roster);
  const nameOf = (c: Combatant) => names.get(c.id) as string;
  const list = pilotableProfiles(roster, actorId, nameOf);
  const actor = roster.find((c) => c.id === actorId);
  // Not acting as anyone yet: the Default entry is a SCRATCH grid you can
  // still hand-build a weapon in, exactly as before the roster. Weapons
  // only acquire an owner once you act as someone.
  if (!actor) list[NO_ACTOR_PROFILE] = scratchSlots;
  const bindings = characterBindings(roster, nameOf);
  return {
    current: actor ? nameOf(actor) : NO_ACTOR_PROFILE,
    list,
    ...(Object.keys(bindings).length ? { _charBindings: bindings } : {}),
  } as AtkProfiles;
}

/**
 * Only combatants you could plausibly pilot: anyone with a real character
 * sheet, plus whoever you are currently acting as. Listing every nameless
 * statblock as an "attacker profile" is a phase-C picker decision, not
 * something to smuggle in behind the old dropdown.
 */
function pilotableProfiles(
  roster: Combatant[],
  actorId: string | null,
  nameOf: (c: Combatant) => string,
): Record<string, Slot[]> {
  const list: Record<string, Slot[]> = {};
  for (const c of roster) {
    if (c.kind === 'character' || c.id === actorId) list[nameOf(c)] = projectLoadout(c).slots;
  }
  return list;
}

/**
 * Legacy export/import still speaks _charBindings, so synthesise it from
 * the roster. Nothing READS it back any more (a combatant is its own
 * binding); it exists so a v3 session file still round-trips a character.
 */
function characterBindings(
  roster: Combatant[],
  nameOf: (c: Combatant) => string,
): Record<string, { character: unknown }> {
  const bindings: Record<string, { character: unknown }> = {};
  for (const c of roster) {
    if (c.kind === 'character') bindings[nameOf(c)] = { character: c.sheet };
  }
  return bindings;
}

/** Resolve a dropdown profile name back to the combatant it names. */
export function combatantIdForProfile(roster: Combatant[], name: string): string | null {
  const names = profileNames(roster);
  for (const c of roster) if (names.get(c.id) === name) return c.id;
  return null;
}

/**
 * Write an edited AtkProfiles back onto the roster.
 *
 * Only the ACTOR's grid can change through this path - every writer
 * (slot cut/copy/paste/drag, reload, the ammo deduction after an attack)
 * edits the profile currently on screen. Other entries are read-only
 * projections, so re-deriving them would be pure waste.
 */
export function applyLegacyAtkProfiles(roster: Combatant[], actorId: string | null, next: AtkProfiles): Combatant[] {
  if (!actorId) return roster; // No actor - the Default grid is scratch space.
  const names = profileNames(roster);
  return roster.map((c) => {
    if (c.id !== actorId) return c;
    const slots = next.list?.[names.get(c.id) as string];
    return slots ? applyLoadoutSlots(c, slots) : c;
  });
}
